'use client';

import { useRef } from 'react';
import { useWheelToHorizontalScroll } from '@/hooks/useWheelToHorizontalScroll';
import {
  HorizontalContentCards,
  type HorizontalContentCardsProps,
} from './HorizontalContentCards';

/**
 * Horizontally scrollable rail variant of `HorizontalContentCards`.
 *
 * Wheel behavior is delegated to the shared `useWheelToHorizontalScroll` hook
 * used by `SideHeaderCardsRow`'s `ScrollRail` — one place decides how a
 * vertical wheel tick becomes a horizontal `scrollBy({ behavior: 'smooth' })`,
 * how trackpad `deltaX` gestures pass through, how Firefox line-mode delta
 * is normalized, and when to bail because the row doesn't overflow. Keeping
 * both redesign rails on the same hook means the wheel feel stays identical
 * as we tune it.
 *
 * Layout is unchanged from the earlier POC that was approved: cards sized so
 * the next card *peeks* at the right edge (the strongest wordless "more here"
 * affordance), the scrollbar is hidden, and a sticky `::after` pseudo puts a
 * subtle darker vignette over the right edge.
 */

const SCROLL_ROW_CLASS = [
  'flex flex-nowrap overflow-x-auto gap-6 snap-x snap-proximity',
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
  '[&>*]:flex-shrink-0 [&>*]:snap-start',
  '[&>*]:w-[85%] md:[&>*]:w-[calc((100%-1.5rem)/2.2)] lg:[&>*]:w-[calc((100%-1.5rem)/3.2)]',
  "after:content-[''] after:sticky after:right-0 after:top-0 after:-ml-[140px]",
  'after:h-full after:w-[140px] after:flex-shrink-0 after:pointer-events-none',
  'after:bg-gradient-to-l after:from-black/40 after:via-black/15 after:to-transparent',
].join(' ');

export type HorizontalScrollableContentCardsProps = Omit<
  HorizontalContentCardsProps,
  'gridRef'
>;

export function HorizontalScrollableContentCards(
  props: HorizontalScrollableContentCardsProps,
) {
  const rowRef = useRef<HTMLDivElement>(null);
  useWheelToHorizontalScroll(rowRef);

  const mergedGridClassName = props.gridClassName
    ? `${SCROLL_ROW_CLASS} ${props.gridClassName}`
    : SCROLL_ROW_CLASS;

  return (
    <HorizontalContentCards
      {...props}
      gridRef={rowRef}
      gridClassName={mergedGridClassName}
    />
  );
}
